/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { DeliveryOptionsCartLineAddress } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { PriceComponent } from '@msdyn365-commerce/components';
import { CartLine, DeliveryOption } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import DescriptionComponent from './checkout-delivery-options.description';
import RadioButtonComponent from './checkout-cartLine-delivery-options-grp';

export interface ICheckoutCartLineDeliveryOptionsRadioButtonProps {
    className: string;
    moduleId: string;
    ariaSetSize: number;
    priceFree: string;
    ariaPosInSet: number;
    selectedItemId: string;
    cartLines: CartLine[];
    deliveryOptions: DeliveryOption[];
    typeName: string;
    context: ICoreContext<IGeneric<IAny>>;
    groupName: string; // Add groupName prop
    groupDeliveryOption?: DeliveryOptionsCartLineAddress;
    ariaLabel?: string;
    value?: string;
    cartLineId?: string;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>, cartLines: CartLine[], cartLineId?: string): void;
}

/**
 * Represents a radio button component for checkout cart line delivery options.
 * @param {Object} props - The component props.
 * @param {boolean} props.isChecked - Indicates whether the radio button is checked.
 * @param {string} props.value - The value of the radio button.
 * @param {number} props.ariaSetSize - The total number of radio buttons in the set.
 * @param {number} props.ariaPosInSet - The position of the radio button in the set.
 * @param {string} props.ariaLabel - The ARIA label for the radio button.
 * @param {string} props.telemetryContent - The telemetry content for the radio button.
 * @param {Function} props.onChange - The event handler for when the radio button is changed.
 * @returns {JSX.Element} The rendered radio button component.
 */
const CheckoutCartLineDeliveryOptionsRadio: React.FC<ICheckoutCartLineDeliveryOptionsRadioButtonProps> = ({
    className,
    moduleId,
    ariaSetSize,
    ariaPosInSet,
    ariaLabel,
    telemetryContent,
    deliveryOptions,
    priceFree,
    selectedItemId,
    typeName,
    groupName, // Destructure groupName prop
    context,
    cartLines,
    cartLineId,
    groupDeliveryOption,
    onChange
}) => {
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.DeliveryOptions);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    console.log(attributes);
    const channelConfig = context.actionContext.requestContext.channel;
    const count = deliveryOptions.length;
    if (count === 0) {
        return null;
    }
    const cartLinesRadio: CartLine[] = [];
    if (groupDeliveryOption !== undefined && cartLines !== undefined) {
        groupDeliveryOption?.cartLineIds?.map(cartLineId => {
            const tempCartLine = cartLines?.find(cartLine => cartLineId === cartLine?.LineId?.toString());
            if (tempCartLine !== undefined) {
                cartLinesRadio.push(tempCartLine);
            }
        });
    }

    const changeCurrentDeliveryOption = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (cartLinesRadio !== undefined && cartLinesRadio.length > 0) {
                onChange(event, cartLinesRadio, undefined);
            } else {
                onChange(event, cartLines, cartLineId);
            }
        },
        [onChange, cartLinesRadio, cartLineId]
    );
    // const selectedDeliveryOptionItem = deliveryOptions.length > 0 ? deliveryOptions[0].Code || '' : ''; // By default, select first delivery
    const deliveryClassName = `${className}__radio-btn-grp`;
    const optionClassName = `${deliveryClassName}-opt`;
    const response = deliveryOptions.map((deliveryOption: DeliveryOption, index: number) => (
        <div key={index} className={optionClassName}>
            <RadioButtonComponent
                {...{
                    groupName: groupName,
                    isChecked: deliveryOption.Code === selectedItemId,
                    value: deliveryOption.Code,
                    ariaSetSize: count,
                    ariaPosInSet: index + 1,
                    ariaLabel: `${deliveryOption.Description!}`,
                    onChange: changeCurrentDeliveryOption,
                    telemetryContent: telemetryContent,
                    className: `${optionClassName}-btn`
                }}
            />
            <DescriptionComponent
                {...{
                    description: deliveryOption.Description,
                    className: `${optionClassName}-dsc`
                }}
            />
            <PriceComponent
                id={moduleId}
                typeName={typeName}
                context={context}
                className={`${optionClassName}-pr`}
                data={{ price: { CustomerContextualPrice: get(deliveryOption, 'ShippingChargeAmount', 0) } }}
                freePriceText={priceFree}
                currencyCode={channelConfig?.Currency}
            />
        </div>
    ));

    return <div className={deliveryClassName}>{response}</div>;
};

export default CheckoutCartLineDeliveryOptionsRadio;
